import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const getDefaultState = () => {
  return {
    logEntries: [],
    logEntryCount: 0,
  };
};

const state = getDefaultState();

/**
 * Defines the fields which are requested for a log entry list view
 * used for paginated log entry queries
 * @type {DocumentNode}
 */
const LOG_ENTRY_LIST_REQUEST_FIELDS = gql`
  fragment logEntryListFields on LogEntry {
    _id
    timestamp
    event
    data
    deviceInfo {
      model
      platform
      osVersion
      manufacturer
      appVersion
    }
    user {
      _id
      firstname
      lastname
      email
    }
    createdAt
    updatedAt
  }
`;

/**
 * Getters
 */
const getters = {
  getLogEntries: (state) => state.logEntries,
  getLogEntryCount: (state) => state.logEntryCount,
};

/**
 * Mutations
 */
const mutations = {
  SET_LOG_ENTRIES(state, logEntries) {
    state.logEntries = logEntries;
  },
  SET_LOG_ENTRY_COUNT(state, count) {
    state.logEntryCount = count;
  },
  ADD_OR_UPDATE_LOG_ENTRY(state, logEntry) {
    const index = state.logEntries.findIndex((entry) => entry._id === logEntry._id);
    if (index !== -1) {
      state.logEntries.splice(index, 1, logEntry);
    } else {
      state.logEntries.push(logEntry);
    }
  },
  CLEAR_LOG_ENTRIES(state) {
    state.logEntries = [];
    state.logEntryCount = 0;
  },
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch paginated log entries
   * @param {Object} context - Vuex context
   * @param {Object} params - Parameters object
   * @param {Object} params.pagination - Pagination parameters
   * @param {Object} params.sorting - Sorting parameters
   * @param {Object} params.filters - Filter parameters
   */
  async fetchLogEntriesPaginated({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const { data, errors } = await apolloClient.query({
        query: gql`
          query logEntriesPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: LogEntryFilterInput
          ) {
            logEntriesPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
              logEntries {
                ...logEntryListFields
              }
              logEntryCount
            }
          }
          ${LOG_ENTRY_LIST_REQUEST_FIELDS}
        `,
        variables: {
          pagination,
          sorting,
          filters,
        },
      });

      if (errors) {
        return errors;
      }

      commit('SET_LOG_ENTRIES', data.logEntriesPaginated.logEntries);
      commit('SET_LOG_ENTRY_COUNT', data.logEntriesPaginated.logEntryCount);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * Clear log entries from store
   * @param {Object} context - Vuex context
   */
  clearLogEntries({ commit }) {
    commit('CLEAR_LOG_ENTRIES');
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
